// @ts-check
import { formatDistanceToNow } from 'date-fns';
import { Edit2, Trash2, User } from 'lucide-react';
import { useState } from 'react';

const TaskCommentItem = ({ comment, currentUserId, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.comment_text);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isOwnComment = comment.user_id === currentUserId;

  const handleUpdate = async () => {
    if (!editText.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onUpdate(comment.id, editText);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating comment:', error);
      alert('Failed to update comment');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this comment?')) return;

    try {
      await onDelete(comment.id);
    } catch (error) {
      console.error('Error deleting comment:', error);
      alert('Failed to delete comment');
    }
  };

  return (
    <div className="flex gap-3 p-3 bg-muted/50 rounded-lg">
      {/* Avatar */}
      <div className="flex-shrink-0">
        <div className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
          {comment.user_name?.charAt(0).toUpperCase() || <User size={16} />}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
          <div>
            <span className="font-medium text-sm text-foreground">{comment.user_name || 'Unknown User'}</span>
            <span className="text-xs text-muted-foreground ml-2">
              {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
            </span>
          </div>

          {/* Actions (only for own comments) */}
          {isOwnComment && !isEditing && (
            <div className="flex gap-1">
              <button
                onClick={() => setIsEditing(true)}
                className="p-1 text-muted-foreground/70 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                title="Edit"
              >
                <Edit2 size={14} />
              </button>
              <button
                onClick={handleDelete}
                className="p-1 text-muted-foreground/70 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                title="Delete"
              >
                <Trash2 size={14} />
              </button>
            </div>
          )}
        </div>

        {/* Comment Text */}
        {isEditing ? (
          <div className="space-y-2">
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="w-full px-3 py-2 border border-input rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={3}
              disabled={isSubmitting}
            />
            <div className="flex gap-2">
              <button
                onClick={handleUpdate}
                disabled={isSubmitting || !editText.trim()}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={() => {
                  setIsEditing(false);
                  setEditText(comment.comment_text);
                }}
                disabled={isSubmitting}
                className="px-3 py-1 bg-accent text-foreground/80 text-sm rounded-lg hover:bg-muted-foreground/30 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-foreground/80 whitespace-pre-wrap">{comment.comment_text}</p>
        )}
      </div>
    </div>
  );
};

export default TaskCommentItem;
